import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Text } from 'barte-design-system';
import { X } from '@phosphor-icons/react';
import './MccTagInput.css';

// Lista mock de MCCs válidos no sistema. Códigos numéricos de 4 dígitos
// seguindo padrão ISO 18245 (MCCs reais usados pelas bandeiras).
export const MCC_VALIDOS: Record<string, string> = {
  '5411': 'Mercados e supermercados',
  '5812': 'Restaurantes',
  '5814': 'Fast food',
  '5912': 'Farmácias e drogarias',
  '5999': 'Lojas de varejo especializado',
  '7298': 'Spas e serviços de bem-estar',
  '7372': 'Software e serviços de tecnologia',
  '8011': 'Médicos e profissionais de saúde',
  '8021': 'Dentistas e ortodontistas',
  '8062': 'Hospitais',
  '8211': 'Educação e escolas',
  '8398': 'Organizações filantrópicas',
  '8999': 'Serviços profissionais não classificados',
  '5732': 'Eletrônicos e eletrodomésticos',
  '5944': 'Joalherias e relojoarias',
  '5651': 'Vestuário e moda',
  '4121': 'Transporte por aplicativo / táxis',
  '4511': 'Companhias aéreas',
  '7011': 'Hotéis e hospedagem',
  '5541': 'Postos de combustível',
};

interface DropdownRect { top: number; left: number; width: number; }

interface McсTagInputProps {
  label?: string;
  value: string[];
  onChange: (mccs: string[]) => void;
  error?: string;
  /** Modo read-only: esconde o input de novo MCC e o X de remover. */
  readOnly?: boolean;
}

export function MccTagInput({
  label = 'MCCs',
  value,
  onChange,
  error,
  readOnly = false,
}: McсTagInputProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState<DropdownRect | null>(null);
  const [touchedInvalid, setTouchedInvalid] = useState<string | null>(null);
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);

  const allEntries = Object.entries(MCC_VALIDOS);
  const q = query.trim().toLowerCase();
  const suggestions = q.length === 0
    ? allEntries.filter(([code]) => !value.includes(code)).slice(0, 8)
    : allEntries
        .filter(([code, desc]) =>
          !value.includes(code) &&
          (code.includes(q) || desc.toLowerCase().includes(q))
        )
        .slice(0, 8);

  function updateRect() {
    if (fieldRef.current) {
      const r = fieldRef.current.getBoundingClientRect();
      setDropdownRect({ top: r.bottom + 4, left: r.left, width: r.width });
    }
  }

  function addMcc(code: string) {
    setTouchedInvalid(null);
    if (!code) return;
    if (!MCC_VALIDOS[code]) {
      setTouchedInvalid(`MCC ${code} não encontrado no sistema.`);
      return;
    }
    if (value.includes(code)) return;
    onChange([...value, code]);
    setQuery('');
    setHighlighted(0);
    inputRef.current?.focus();
  }

  function removeMcc(code: string) {
    onChange(value.filter((c) => c !== code));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, suggestions.length - 1));
      setOpen(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && suggestions[highlighted]) {
        addMcc(suggestions[highlighted][0]);
      } else if (/^\d{4}$/.test(query.trim())) {
        addMcc(query.trim());
      } else if (query.trim()) {
        setTouchedInvalid(`"${query.trim()}" não é um MCC válido. Digite um código de 4 dígitos ou escolha da lista.`);
      }
    } else if (e.key === ',' ) {
      e.preventDefault();
      if (/^\d{4}$/.test(query.trim())) addMcc(query.trim());
    } else if (e.key === 'Backspace' && query === '' && value.length > 0) {
      onChange(value.slice(0, -1));
      setTouchedInvalid(null);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text');
    const codes = text.split(/[\s,;\r\n]+/).map((c) => c.trim()).filter(Boolean);
    if (codes.length > 1) {
      e.preventDefault();
      const next = [...value];
      for (const code of codes) {
        if (/^\d{4}$/.test(code) && MCC_VALIDOS[code] && !next.includes(code)) {
          next.push(code);
        }
      }
      onChange(next);
      setQuery('');
    }
  }

  const errorMessage = error || touchedInvalid;
  const showDropdown = open && suggestions.length > 0;

  return (
    <div className="mcc-tag-wrapper">
      <Text variant="label-md" className="mcc-tag-label">{label}</Text>
      <div
        ref={fieldRef}
        className={`mcc-tag-field${errorMessage ? ' mcc-tag-field--error' : ''}`}
        onClick={() => { inputRef.current?.focus(); setOpen(true); updateRect(); }}
      >
        {value.map((code) => (
          <span key={code} className="mcc-tag-pill" title={MCC_VALIDOS[code]}>
            <span className="mcc-tag-pill-code">{code}</span>
            {!readOnly && (
              <button
                type="button"
                className="mcc-tag-pill-remove"
                onClick={(e) => { e.stopPropagation(); removeMcc(code); }}
                aria-label={`Remover MCC ${code}`}
              >
                <X size={12} weight="bold" />
              </button>
            )}
          </span>
        ))}
        {!readOnly && (
          <input
            ref={inputRef}
            className="mcc-tag-input"
            placeholder={value.length === 0 ? 'Busque ou cole códigos separados por vírgula' : ''}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); updateRect(); setHighlighted(0); setTouchedInvalid(null); }}
            onFocus={() => { setOpen(true); updateRect(); }}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
          />
        )}
      </div>

      {errorMessage && (
        <Text variant="caption-md" className="mcc-tag-error">{errorMessage}</Text>
      )}

      {showDropdown && dropdownRect && createPortal(
        <div
          className="mcc-tag-dropdown"
          style={{ top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width }}
        >
          {suggestions.map(([code, desc], idx) => (
            <button
              key={code}
              type="button"
              className={`mcc-tag-option${idx === highlighted ? ' is-highlighted' : ''}`}
              onMouseEnter={() => setHighlighted(idx)}
              onMouseDown={(e) => { e.preventDefault(); addMcc(code); }}
            >
              <span className="mcc-tag-option-code">{code}</span>
              <span className="mcc-tag-option-desc">{desc}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
